/**
 * REST-клиент модуля чата (/api/v1/chat*).
 */

import { get, post, put, patch, del, postFormData } from "@/lib/api-client-v2";

export type ChatConversation = {
  id: string;
  type: "direct" | "group";
  title: string | null;
  created_by: string;
  created_at: string;
  is_encrypted: boolean;
  encryption_protocol: string;
  protocol_version: number;
  sender_key_epoch: number;
  participants: Array<{
    user_id: string;
    role: string;
    joined_at: string;
    left_at?: string | null;
    last_read_message_id?: string | null;
  }>;
};

export type ChatMessage = {
  id: string;
  conversation_id: string;
  sender_id: string;
  seq: number;
  client_message_id: string;
  ciphertext_b64: string;
  content_type: string;
  created_at: string;
  reply_to_id?: string | null;
};

export type PrekeyBundle = {
  user_id: string;
  device_id: string;
  identity_key_b64: string;
  signed_prekey_b64: string;
  signed_prekey_sig_b64: string;
  signed_prekey_id: number;
  registration_id: number;
  one_time_prekey_id?: number | null;
  one_time_prekey_b64?: string | null;
};

export type ChatGif = {
  id: string;
  tags?: string[] | null;
  is_active: boolean;
  created_at: string;
};

export type ChatDirectoryUser = {
  id: string;
  username?: string | null;
  display_name: string;
  first_name?: string | null;
  last_name?: string | null;
};

export const chatApi = {
  directoryUsers: (params?: { q?: string; limit?: number }) => {
    const q: Record<string, string | number | boolean> = {};
    if (params?.q?.trim()) q.q = params.q.trim();
    if (params?.limit != null) q.limit = params.limit;
    return get<ChatDirectoryUser[]>("/chat/directory/users", q);
  },

  settings: () => get<{ e2ee_required: boolean }>("/chat/settings"),

  registerDevice: (body: Record<string, unknown>) =>
    put<Record<string, unknown>>("/chat/devices/me", body),

  myDevice: () => get<Record<string, unknown> | null>("/chat/devices/me"),

  prekeyBundle: (userId: string) =>
    get<PrekeyBundle>(`/chat/users/${encodeURIComponent(userId)}/prekey-bundle`),

  conversations: () => get<ChatConversation[]>("/chat/conversations"),

  createDirect: (peer_user_id: string) =>
    post<ChatConversation>("/chat/conversations/direct", { peer_user_id }),

  createGroup: (title: string, member_user_ids: string[]) =>
    post<ChatConversation>("/chat/conversations/group", { title, member_user_ids }),

  getConversation: (id: string) => get<ChatConversation>(`/chat/conversations/${id}`),

  sendMessage: (
    conversationId: string,
    body: {
      client_message_id: string;
      ciphertext_b64: string;
      content_type: string;
      reply_to_id?: string | null;
      sender_key_epoch?: number;
    },
  ) => post<ChatMessage>(`/chat/conversations/${conversationId}/messages`, body),

  syncMessages: (conversationId: string, after_seq = 0, limit = 100) =>
    get<{ items: ChatMessage[]; next_after_seq?: number | null }>(
      `/chat/conversations/${conversationId}/messages`,
      { after_seq, limit },
    ),

  uploadAttachment: (form: FormData) =>
    postFormData<{ id: string; password_protected: boolean }>("/chat/attachments", form),

  downloadAttachmentUrl: (id: string) => `/api/v1/chat/attachments/${id}/download`,

  listGifs: (tag?: string, opts?: { includeInactive?: boolean }) => {
    const q: Record<string, string | number | boolean> = {};
    if (tag) q.tag = tag;
    if (opts?.includeInactive) q.include_inactive = true;
    return get<ChatGif[]>("/chat/gifs", q);
  },

  uploadGif: (file: File, tags: string[]) => {
    const form = new FormData();
    form.append("file", file);
    form.append("tags", JSON.stringify(tags));
    return postFormData<ChatGif>("/chat/gifs", form);
  },

  updateGif: (id: string, body: { tags?: string[]; is_active?: boolean }) =>
    patch<ChatGif>(`/chat/gifs/${id}`, body),

  deactivateGif: (id: string) => del<ChatGif>(`/chat/gifs/${id}`),

  gifContentUrl: (id: string) => `/api/v1/chat/gifs/${id}/content`,

  patchSettings: (body: { e2ee_required?: boolean }) =>
    patch<{ e2ee_required: boolean }>("/chat/settings", body),
};

export function chatWsUrl(token: string): string {
  const proto = window.location.protocol === "https:" ? "wss" : "ws";
  return `${proto}://${window.location.host}/ws/chat?token=${encodeURIComponent(token)}`;
}
